import React from 'react';
import styled from 'styled-components';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import GatsbyLink from 'gatsby-link';

const StyledForm = styled(Form)`
  width: 100%;
  padding: 0 30px;
  margin: 50px auto;
  max-width: 800px;
`;

const StyledInput = styled(Field)`
  font-size: ${({ theme }) => theme.font.size.xs};
  box-sizing: border-box;
  outline: none;
  display: block;
  width: 100%;
  padding: 7px;
  border: none;
  border-bottom: 1px solid #ddd;
  background: transparent;
  margin-bottom: 14px;
  height: 50px;

  ${({ theme }) => theme.mq.desktop} {
    font-size: ${({ theme }) => theme.font.size.s};
  }
`;

const StyledTextArea = styled.textarea`
  font-size: ${({ theme }) => theme.font.size.xs};
  box-sizing: border-box;
  outline: none;
  display: block;
  width: 100%;
  padding: 7px;
  border: none;
  border-bottom: 1px solid #ddd;
  background: transparent;
  margin-bottom: 14px;
  height: 200px;
  resize: none;
  overflow: hidden;

  ${({ theme }) => theme.mq.desktop} {
    font-size: ${({ theme }) => theme.font.size.s};
  }
`;

const removeButtonStyle = styled.button`
  display: inline-block;
  border: none;
  padding: 1rem 2rem;
  margin: 0;
  text-decoration: none;
  line-height: 1;
  cursor: pointer;
  text-align: center;

  :focus {
    outline: 1px solid #fff;
    outline-offset: -4px;
  }

  :active {
    transform: scale(0.99);
  }
`;

const Btn = styled(removeButtonStyle)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.primaryGreener};
  font-size: ${({ theme }) => theme.font.size.xs};
  line-height: 50px;
  height: 50px;
  text-align: center;
  width: 250px;
  cursor: pointer;
  transition: all 0.5s;
  position: relative;
  color: black;

  ${({ theme }) => theme.mq.desktop} {
    font-size: ${({ theme }) => theme.font.size.s};
  }

  :hover {
    background-color: white;
  }

  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    transition: all 0.3s;
  }

  :hover::before {
    opacity: 0;
    transform: scale(0.5, 0.5);
  }

  ::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    opacity: 0;
    transition: all 0.3s;
    border: 1px solid #ababab;
    transform: scale(1.2, 1.2);
  }

  :hover::after {
    opacity: 1;
    transform: scale(1, 1);
  }
`;

const StyledErrorMessage = styled(ErrorMessage)`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: red;
  padding: 7px;
`;

const MapleSyrupStyle = styled(Field)`
  display: none;
`;

const RodoWrapper = styled.div`
  margin: 2rem 0;
  font-size: ${({ theme }) => theme.font.size.xs};
`;

const RodoStyle = styled.div`
  margin: 1rem 0;
  font-size: ${({ theme }) => theme.font.size.xs};

  ${({ theme }) => theme.mq.desktop} {
    font-size: ${({ theme }) => theme.font.size.s};
  }

  p {
    margin-top: 0;
  }
`;

export default function ContactFormCom() {
  const submitRequest = async (values, actions) => {
    const res = await fetch(
      `${process.env.GATSBY_SERVERLESS_BASE}/contactForm`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          tel: values.tel,
          message: values.message,
          mapleSyrup: values.mapleSyrup,
          checkbox: values.checkbox,
        }),
      }
    );

    const text = JSON.parse(await res.text());

    actions.setSubmitting(false);

    if (res.status >= 400 && res.status < 600) {
      actions.setStatus({
        sent: false,
        msg: text.message,
      });
    } else {
      actions.resetForm();
      actions.setStatus({
        sent: true,
        msg: `Wiadomość została wysłana. Dziękujemy!`,
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          email: '',
          name: '',
          tel: '',
          message: '',
          checkbox: false,
          mapleSyrup: '',
        }}
        validate={(values) => {
          const errors = {};

          if (!values.name) {
            errors.name = 'Wpisz swoje imię i nazwisko';
          }

          if (!values.message) {
            errors.message = 'Wpisz treść wiadomości';
          }

          if (!values.email) {
            errors.email = 'Wpisz swój adres emailowy';
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
          ) {
            errors.email = 'Nieprawidły adres mailowy';
          }

          if (!values.checkbox) {
            errors.checkbox =
              'Zgoda jest wymagana abyśmy mogli się skontaktować.';
          }

          return errors;
        }}
        onSubmit={(values, actions) => {
          submitRequest(values, actions);
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          status,
        }) => (
          <StyledForm onSubmit={handleSubmit}>
            <h2>Skontaktuj się z nami!</h2>
            <StyledErrorMessage name="name" component="div" />
            <StyledInput
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              type="text"
              name="name"
              placeholder="Imię i nazwisko"
            />
            <StyledErrorMessage name="email" component="div" />
            <StyledInput
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              type="email"
              name="email"
              placeholder="Email"
            />
            <StyledInput
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.tel}
              type="text"
              name="tel"
              placeholder="Numer telefonu"
            />
            <StyledErrorMessage name="message" component="div" />
            <StyledTextArea
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.message}
              name="message"
              placeholder="Wiadomość"
            />
            <MapleSyrupStyle
              type="mapleSyrup"
              name="mapleSyrup"
              value={values.mapleSyrup}
              onChange={handleChange}
            />
            <RodoWrapper>
              Podanie danych jest dobrowolne, przed ich podaniem zapoznaj się z
              zasadami naszej Polityki prywatności i polityki cookies dostępnej{' '}
              <GatsbyLink to="/polityka-prywatnosci">tutaj</GatsbyLink>.
              <RodoStyle>
                <StyledErrorMessage name="checkbox" component="div" />
                <p>
                  <Field
                    id="checkbox"
                    type="checkbox"
                    name="checkbox"
                    checked={values.checkbox}
                    onChange={() => setFieldValue('checkbox', !values.checkbox)}
                  />{' '}
                  Oświadczam iż zapoznałam/łem się z zasadami polityki
                  prywatności.
                </p>
              </RodoStyle>
            </RodoWrapper>
            {status && status.msg && (
              <p
                className={`alert ${
                  status.sent ? 'alert-success' : 'alert-error'
                }`}
              >
                {status.msg}
              </p>
            )}
            <Btn type="submit" disabled={isSubmitting}>
              {isSubmitting ? `Wysyła...` : `Wyślij wiadomość`}
            </Btn>
          </StyledForm>
        )}
      </Formik>
    </>
  );
}
